import { devError } from '../utils/devLogger';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

class ColdStorageService {
  
  async moveToDeletedRecords(collectionName, documentId, documentData, deletedBy, reason = 'Manual deletion') {
    try {
      const coldStorageDoc = {
        originalCollection: collectionName,
        originalId: documentId,
        data: documentData,
        deletedAt: serverTimestamp(),
        deletedBy: deletedBy,
        deleteReason: reason,
        restorable: true
      };

      const coldStorageRef = await addDoc(collection(db, 'cold_storage'), coldStorageDoc);
      
      return {
        success: true,
        coldStorageId: coldStorageRef.id,
        message: 'Record moved to cold storage successfully'
      };
    } catch (error) {
      devError('Error moving record to cold storage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async moveUserToColdStorage(userId, userProfile, deletedBy, reason = 'Manual user deletion') {
    try {
      const result = await this.moveToDeletedRecords('users', userId, userProfile, deletedBy, reason);
      
      if (result.success) {
        await this.moveUserSubcollectionsToColdStorage(userId, result.coldStorageId, deletedBy);
      }
      
      return result;
    } catch (error) {
      devError('Error moving user to cold storage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async moveUserSubcollectionsToColdStorage(userId, parentColdStorageId, deletedBy) {
    const subcollections = ['documents', 'attendance', 'progress', 'messages'];
    
    for (const subcollection of subcollections) {
      try {
        const subcollectionRef = collection(db, 'users', userId, subcollection);
        const snapshot = await getDocs(subcollectionRef);
        
        for (const docSnapshot of snapshot.docs) {
          await this.moveToDeletedRecords(
            `users/${userId}/${subcollection}`,
            docSnapshot.id,
            docSnapshot.data(),
            deletedBy,
            `User deletion - ${subcollection} data`
          );
        }
      } catch (error) {
        devError(`Error moving ${subcollection} to cold storage:`, error);
      }
    }
  }

  async getDeletedRecords(collectionName = null) {
    try {
      let queryRef = collection(db, 'cold_storage');
      
      if (collectionName) {
        queryRef = query(queryRef, where('originalCollection', '==', collectionName));
      }
      
      const snapshot = await getDocs(queryRef);
      const deletedRecords = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return {
        success: true,
        records: deletedRecords
      };
    } catch (error) {
      devError('Error fetching deleted records:', error);
      return {
        success: false,
        error: error.message,
        records: []
      };
    }
  }

  async restoreRecord(coldStorageId, restoredBy) {
    try {
      const coldStorageDocRef = doc(db, 'cold_storage', coldStorageId);
      const coldStorageDoc = await getDoc(coldStorageDocRef);
      
      if (!coldStorageDoc.exists()) {
        throw new Error('Cold storage record not found');
      }
      
      const recordData = coldStorageDoc.data();
      
      if (!recordData.restorable) {
        throw new Error('This record is not restorable');
      }

      const restoredData = {
        ...recordData.data,
        restoredAt: serverTimestamp(),
        restoredBy: restoredBy
      };

      await updateDoc(coldStorageDocRef, {
        restored: true,
        restoredAt: serverTimestamp(),
        restoredBy: restoredBy
      });
      
      return {
        success: true,
        data: restoredData,
        originalCollection: recordData.originalCollection,
        originalId: recordData.originalId,
        message: 'Record restored successfully'
      };
    } catch (error) {
      devError('Error restoring record:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async permanentlyDelete(coldStorageId, deletedBy, adminPassword) {
    try {
      const coldStorageDocRef = doc(db, 'cold_storage', coldStorageId);
      const coldStorageDoc = await getDoc(coldStorageDocRef);
      
      if (!coldStorageDoc.exists()) {
        throw new Error('Cold storage record not found');
      }
      
      await updateDoc(coldStorageDocRef, {
        permanentlyDeleted: true,
        permanentlyDeletedAt: serverTimestamp(),
        permanentlyDeletedBy: deletedBy,
        restorable: false
      });
      
      return {
        success: true,
        message: 'Record permanently deleted from cold storage'
      };
    } catch (error) {
      devError('Error permanently deleting record:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getColdStorageStats() {
    try {
      const snapshot = await getDocs(collection(db, 'cold_storage'));
      const records = snapshot.docs.map(doc => doc.data());
      
      const stats = {
        total: records.length,
        restorable: records.filter(r => r.restorable && !r.restored).length,
        restored: records.filter(r => r.restored).length,
        permanentlyDeleted: records.filter(r => r.permanentlyDeleted).length,
        byCollection: {}
      };
      
      records.forEach(record => {
        const collection = record.originalCollection;
        if (!stats.byCollection[collection]) {
          stats.byCollection[collection] = 0;
        }
        stats.byCollection[collection]++;
      });
      
      return {
        success: true,
        stats: stats
      };
    } catch (error) {
      devError('Error getting cold storage stats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async archiveOldRecords(daysOld = 90) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      
      const snapshot = await getDocs(collection(db, 'cold_storage'));
      const oldRecords = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.deletedAt?.toDate() < cutoffDate && !data.archived) {
          oldRecords.push(doc.id);
        }
      });
      
      for (const recordId of oldRecords) {
        await updateDoc(doc(db, 'cold_storage', recordId), {
          archived: true,
          archivedAt: serverTimestamp()
        });
      }
      
      return {
        success: true,
        archivedCount: oldRecords.length,
        message: `${oldRecords.length} old records archived`
      };
    } catch (error) {
      devError('Error archiving old records:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

const coldStorageService = new ColdStorageService();
export default coldStorageService;